<template>
    <div class="financial-profile-container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>الملف المالي لولي الأمر</h2>
          </div>
        </template>
  
        <!-- Parent Selection -->
        <div class="parent-selection-section">
          <el-alert
            title="الخطوة الأولى: تحديد ولي الأمر"
            type="success"
            description="ابدأ بالبحث عن ولي الأمر لعرض كشف حسابه المفصل."
            :closable="false"
            show-icon
          />
          <div class="search-bar">
            <el-select
              v-model="selectedParentId"
              filterable
              remote
              :remote-method="searchParents"
              :loading="parentsLoading"
              placeholder="ابحث بالاسم أو رقم الهاتف"
              @change="fetchProfile"
              style="width: 100%;"
              clearable
            >
              <el-option v-for="parent in parentsList" :key="parent.id" :label="`${parent.first_name} ${parent.last_name}`" :value="parent.id" />
            </el-select>
          </div>
        </div>
  
        <!-- Profile Display -->
        <div v-if="profileData" v-loading="profileLoading" class="profile-display-section">
          <el-divider />
          
          <div id="profile-printable">
              <div class="profile-header">
                  <h3>كشف حساب ولي الأمر: {{ profileData.parent.first_name }} {{ profileData.parent.last_name }}</h3>
                  <el-button class="no-print" type="primary" @click="printProfile"><el-icon><Printer /></el-icon> طباعة الكشف</el-button>
              </div>
      
              <el-descriptions :column="3" border class="summary-descriptions">
                  <el-descriptions-item label="إجمالي الفواتير">{{ profileData.summary.total_invoiced.toFixed(2) }} دينار</el-descriptions-item>
                  <el-descriptions-item label="إجمالي المدفوعات">{{ profileData.summary.total_paid.toFixed(2) }} دينار</el-descriptions-item>
                  <el-descriptions-item label="الرصيد النهائي">
                      <strong :class="profileData.summary.balance > 0 ? 'debit' : 'credit'">
                          {{ profileData.summary.balance.toFixed(2) }} دينار
                      </strong>
                  </el-descriptions-item>
              </el-descriptions>
      
              <el-divider>سجل الفواتير</el-divider>
      
              <div v-for="invoice in profileData.parent.invoices" :key="invoice.id" class="invoice-card">
                  <el-card shadow="never">
                      <template #header>
                          <div class="invoice-card-header">
                              <span>الفاتورة #: {{ invoice.invoice_number }}</span>
                              <el-tag :type="getStatusTagType(invoice.status)">{{ invoice.status }}</el-tag>
                          </div>
                      </template>
                      <el-descriptions :column="2" size="small">
                          <el-descriptions-item label="تاريخ الإصدار">{{ formatDate(invoice.issue_date) }}</el-descriptions-item>
                          <el-descriptions-item label="المبلغ النهائي">{{ invoice.final_amount }} دينار</el-descriptions-item>
                          <el-descriptions-item label="المبلغ المدفوع">{{ invoice.paid_amount }} دينار</el-descriptions-item>
                          <el-descriptions-item label="المبلغ المتبقي">{{ invoice.remaining_amount }} دينار</el-descriptions-item>
                      </el-descriptions>
                  </el-card>
              </div>
          </div>
        </div>
         <div v-else-if="!profileLoading">
          <el-empty description="الرجاء اختيار ولي أمر لعرض ملفه المالي." />
        </div>
    
      </el-card>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage } from 'element-plus';
  import { Search, Printer } from '@element-plus/icons-vue';
  import debounce from 'lodash/debounce';
  
  export default {
    name: 'ParentFinancialProfile',
    components: { Search, Printer },
    data() {
      return {
        api: null,
        parentsList: [],
        parentsLoading: false,
        selectedParentId: null,
        
        profileData: null,
        profileLoading: false,
      };
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.searchParents = debounce(this.searchParents, 500);
    },
    methods: {
      async searchParents(query) {
        if (!query) {
          this.parentsList = [];
          return;
        }
        this.parentsLoading = true;
        try {
          const response = await this.api.get('/parents', { params: { search: query } });
          this.parentsList = response.data.data;
        } catch (error) {
          ElMessage.error('Failed to search for parents.');
        } finally {
          this.parentsLoading = false;
        }
      },
      async fetchProfile(parentId) {
        if (!parentId) {
          this.profileData = null;
          return;
        }
        this.profileLoading = true;
        try {
          const response = await this.api.get(`/financial-profile/parent/${parentId}`);
          this.profileData = response.data;
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'Failed to fetch financial profile.');
          this.profileData = null;
        } finally {
          this.profileLoading = false;
        }
      },
      getStatusTagType(status) {
        const statusMap = {
          paid: 'success',
          partially_paid: 'warning',
          overdue: 'danger',
          unpaid: 'info',
        };
        return statusMap[status] || 'info';
      },
      formatDate(dateString) {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
      },
      printProfile() {
          if (!this.profileData) return;
  
          const logoUrl = `${window.location.origin}/logo-school-one.png`;
          const profile = this.profileData;
          
          const invoicesHtml = profile.parent.invoices.map(invoice => `
              <div class="invoice-summary">
                  <div class="invoice-summary-header">
                      <span><strong>الفاتورة #:</strong> ${invoice.invoice_number}</span>
                      <span><strong>الحالة:</strong> ${invoice.status}</span>
                  </div>
                  <table class="summary-table">
                      <tr>
                          <td>تاريخ الإصدار: ${this.formatDate(invoice.issue_date)}</td>
                          <td>المبلغ النهائي: ${invoice.final_amount} دينار</td>
                      </tr>
                      <tr>
                          <td>المبلغ المدفوع: ${invoice.paid_amount} دينار</td>
                          <td>المبلغ المتبقي: ${invoice.remaining_amount} دينار</td>
                      </tr>
                  </table>
              </div>
          `).join('');
  
          const printContent = `
              <html>
              <head>
                  <title>كشف حساب ولي الأمر</title>
                  <style>
                      body { font-family: 'Tajawal', sans-serif; direction: rtl; color: #333; }
                      .print-container { max-width: 800px; margin: auto; padding: 30px; }
                      .print-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #00447C; padding-bottom: 15px; }
                      .print-header h2 { margin: 0; color: #00447C; }
                      .print-header img { max-width: 120px; }
                      .parent-info { margin-top: 20px; }
                      .financial-summary { margin-top: 20px; padding: 15px; background-color: #f8f9fa; border-radius: 8px; }
                      .financial-summary table { width: 100%; }
                      .invoice-list { margin-top: 30px; }
                      .invoice-summary { border: 1px solid #dee2e6; border-radius: 8px; margin-bottom: 15px; overflow: hidden; page-break-inside: avoid; }
                      .invoice-summary-header { background-color: #f1f3f5; padding: 10px; display: flex; justify-content: space-between; font-weight: bold; }
                      .summary-table { width: 100%; }
                      .summary-table td { padding: 8px 10px; width: 50%; }
                      @media print {
                          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                      }
                  </style>
              </head>
              <body>
                  <div class="print-container">
                      <div class="print-header">
                          <h2>كشف حساب ولي الأمر</h2>
                          <img src="${logoUrl}" alt="School Logo">
                      </div>
                      <div class="parent-info">
                          <h3>${profile.parent.first_name} ${profile.parent.last_name}</h3>
                      </div>
                      <div class="financial-summary">
                          <table>
                              <tr>
                                  <td><strong>إجمالي الفواتير:</strong> ${profile.summary.total_invoiced.toFixed(2)} دينار</td>
                                  <td><strong>إجمالي المدفوعات:</strong> ${profile.summary.total_paid.toFixed(2)} دينار</td>
                                  <td><strong>الرصيد النهائي:</strong> ${profile.summary.balance.toFixed(2)} دينار</td>
                              </tr>
                          </table>
                      </div>
                      <div class="invoice-list">
                          <h3 style="border-bottom: 1px solid #ccc; padding-bottom: 5px;">سجل الفواتير</h3>
                          ${invoicesHtml}
                      </div>
                  </div>
              </body>
              </html>
          `;
  
          const printWindow = window.open('', '_blank');
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
              printWindow.print();
              printWindow.close();
          }, 250);
      }
    },
  };
  </script>
  
  <style scoped>
  .financial-profile-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .parent-selection-section .search-bar {
      margin-top: 20px;
      max-width: 500px;
  }
  .profile-display-section {
      margin-top: 20px;
  }
  .profile-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
  }
  .summary-descriptions { margin-bottom: 20px; }
  .debit { color: #F56C6C; }
  .credit { color: #67C23A; }
  .invoice-card {
      margin-bottom: 15px;
  }
  .invoice-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
  }
  </style>
  